import { Play, Pause, RotateCcw, Coffee, Target } from "lucide-react";
import { formatTime, matchMinute, stoppage, PERIOD_LABEL, PERIOD_SHORT } from "@/lib/events";

const END_LABEL = { 1: "Terminar 1ª parte · Intervalo", 2: "Terminar 2ª parte · Prolongamento", 3: "Terminar 1ª parte prol. · Intervalo" };

const Btn = ({ testId, onClick, className, children }) => (
  <button data-testid={testId} onClick={onClick}
    className={`tap h-14 lg:h-16 px-4 lg:px-8 flex items-center justify-center gap-2 lg:gap-3 font-display text-lg lg:text-xl font-bold uppercase tracking-wide ${className}`}>
    {children}
  </button>
);

export const Chronometer = ({ seconds, running, period, reset, interval, onStart, onPause, onReset, onPeriod, onEndHalf, onStartSecond, onShootout, cup }) => {
  const next = period + 1;
  const extra = stoppage(seconds, period, reset);
  if (interval != null)
    return (
      <section data-testid="halftime-panel" className="sticky top-16 z-30 lg:static border border-[#5AC8FA]/50 bg-[#0d1a20] p-3 sm:p-6 lg:p-8">
        <div className="flex items-center gap-2 mb-1 lg:mb-2">
          <span className="w-2.5 h-2.5 rounded-full bg-[#5AC8FA] pulse-dot" />
          <span className="label-caps text-[#5AC8FA]">Intervalo</span>
          <span className="label-caps ml-auto">{PERIOD_LABEL[period]}: {matchMinute(seconds, period, reset)}</span>
        </div>
        <div className="flex items-center justify-between gap-3 lg:block">
          <div data-testid="halftime-clock" className="font-display font-bold tabular-nums leading-none text-6xl sm:text-8xl lg:text-[8rem] tracking-tight text-[#5AC8FA]">
            {formatTime(interval)}
          </div>
          <Btn testId={next === 2 ? "start-second-half" : `start-period-${next}`} onClick={onStartSecond} className="bg-[#CCFF00] text-black hover:brightness-110 lg:mt-6 lg:w-full">
            <Play size={22} /> <span className="hidden sm:inline">Iniciar</span> {PERIOD_LABEL[next]}
          </Btn>
        </div>
        <p className="text-xs sm:text-sm text-zinc-400 mt-2">Pode registar substituições (não contam como oportunidade) e cartões no intervalo.</p>
      </section>
    );
  return (
    <section data-testid="chronometer" className="sticky top-16 z-30 lg:static border border-white/15 bg-[#131313] p-3 sm:p-6 lg:p-8 shadow-[0_8px_24px_rgba(0,0,0,0.6)] lg:shadow-none">
      <div className="flex items-center justify-between mb-1 lg:mb-2">
        <div className="flex items-center gap-2">
          <span className={`w-2.5 h-2.5 rounded-full ${running ? "bg-[#FF3B30] pulse-dot" : "bg-zinc-600"}`} />
          <span data-testid="clock-status" className="label-caps">{running ? "Em jogo" : seconds > 0 ? "Pausado" : "Pronto"}</span>
        </div>
        <div className="flex border border-white/15">
          {(cup && period >= 2 ? [1, 2, 3, 4] : [1, 2]).map((p) => (
            <button key={p} data-testid={`period-${p}`} onClick={() => onPeriod(p)}
              className={`tap px-3 lg:px-4 h-8 lg:h-9 text-xs font-bold uppercase tracking-widest ${period === p ? "bg-white text-black" : "text-zinc-400 hover:bg-white/5"}`}>
              {p <= 2 ? <>{p}ª <span className="hidden sm:inline">Parte</span></> : PERIOD_SHORT[p]}
            </button>
          ))}
        </div>
      </div>
      <div className="flex items-center justify-between gap-3 lg:block">
        <div className="min-w-0">
          <div data-testid="clock-display" className="font-display font-bold tabular-nums leading-none text-6xl sm:text-8xl lg:text-[8rem] xl:text-[9rem] tracking-tight">
            {formatTime(seconds)}
          </div>
          <p data-testid="clock-minute" className={`mt-1 lg:mt-2 font-display text-base lg:text-2xl font-bold uppercase ${extra ? "text-[#FF9F0A]" : "text-zinc-500"}`}>
            Min. {matchMinute(seconds, period, reset)}{extra ? " · Compensação" : ""}
          </p>
        </div>
        <div className="flex gap-2 lg:gap-3 lg:mt-6 shrink-0">
          {running ? (
            <Btn testId="timer-pause" onClick={onPause} className="bg-white text-black hover:bg-zinc-200 lg:flex-1"><Pause size={22} /> Parar</Btn>
          ) : (
            <Btn testId="timer-start" onClick={onStart} className="bg-[#CCFF00] text-black hover:brightness-110 lg:flex-1">
              <Play size={22} /> {seconds > 0 ? "Retomar" : "Iniciar"}
            </Btn>
          )}
          <Btn testId="timer-reset" onClick={onReset} className="border border-white/20 text-white hover:bg-white/10 w-14 lg:w-auto px-0 lg:px-8">
            <RotateCcw size={20} /> <span className="hidden lg:inline">Reiniciar</span>
          </Btn>
        </div>
      </div>
      <div className="flex flex-col sm:flex-row gap-2 mt-3 lg:mt-4 empty:hidden">
        {END_LABEL[period] && (cup || period === 1) && seconds > 0 && (
          <button data-testid={period === 1 ? "end-first-half" : `end-period-${period}`} onClick={onEndHalf}
            className="tap flex-1 h-11 lg:h-12 px-3 border border-[#5AC8FA]/60 text-[#5AC8FA] font-display text-base lg:text-lg font-bold uppercase flex items-center justify-center gap-2 hover:bg-[#5AC8FA] hover:text-black">
            <Coffee size={18} /> {END_LABEL[period]}
          </button>
        )}
        {cup && period >= 2 && seconds > 0 && (
          <button data-testid="start-shootout" onClick={onShootout}
            className="tap flex-1 h-11 lg:h-12 px-3 border border-[#FF9F0A]/60 text-[#FF9F0A] font-display text-base lg:text-lg font-bold uppercase flex items-center justify-center gap-2 hover:bg-[#FF9F0A] hover:text-black">
            <Target size={18} /> Penáltis
          </button>
        )}
      </div>
    </section>
  );
};
